import { useRef, useState } from 'react';
import EmailEditor, { EditorRef } from 'react-email-editor';

interface EmailEditorComponentProps {
  initialDesign?: any; // JSON do Unlayer
  onSave: (design: any, html: string) => void;
  onCancel: () => void;
}

export default function EmailEditorComponent({ initialDesign, onSave, onCancel }: EmailEditorComponentProps) {
  const emailEditorRef = useRef<EditorRef>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  const onReady = () => {
    // Editor está pronto
    if (initialDesign && emailEditorRef.current) {
      emailEditorRef.current.editor?.loadDesign(initialDesign);
    }
  };

  const handleSave = () => {
    if (!emailEditorRef.current) return;

    emailEditorRef.current.editor?.exportHtml((data: any) => {
      const { design, html } = data;
      onSave(design, html);
    });
  };

  const handlePreview = () => {
    if (!emailEditorRef.current) return;

    emailEditorRef.current.editor?.exportHtml((data: any) => {
      const { html } = data;
      setPreviewHtml(html);
      setShowPreview(true);
    });
  };

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="bg-white border-b px-4 py-3 flex justify-between items-center">
        <h2 className="text-lg font-semibold">Editor de Email Visual</h2>
        <div className="flex gap-2">
          <button
            onClick={handlePreview}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            👁️ Preview HTML
          </button>
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            💾 Salvar Template
          </button>
        </div>
      </div>

      {/* Unlayer Email Editor */}
      <div className="flex-1">
        <EmailEditor
          ref={emailEditorRef}
          onReady={onReady}
          minHeight="calc(100vh - 64px)"
          options={{
            displayMode: 'email',
            locale: 'pt-BR',
            appearance: {
              theme: 'light',
            },
            features: {
              textEditor: {
                spellChecker: true,
              },
            },
            tools: {
              form: {
                enabled: true,
              },
            },
            mergeTags: {
              nome: {
                name: 'Nome',
                value: '{{nome}}',
                sample: 'João Silva',
              },
              email: {
                name: 'Email',
                value: '{{email}}',
                sample: 'joao@exemplo.com',
              },
              telefone: {
                name: 'Telefone',
                value: '{{telefone}}',
                sample: '(11) 99999-9999',
              },
              produto: {
                name: 'Produto',
                value: '{{produto}}',
                sample: 'Nome do Produto',
              },
              preco: {
                name: 'Preço',
                value: '{{preco}}',
                sample: 'R$ 99,90',
              },
            },
          }}
        />
      </div>

      {/* Modal de Preview HTML */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Preview do HTML Gerado</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* HTML Code */}
              <div>
                <h4 className="font-semibold mb-2">Código HTML:</h4>
                <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-auto max-h-[600px] text-xs">
                  {previewHtml}
                </pre>
              </div>

              {/* Visual Preview */}
              <div>
                <h4 className="font-semibold mb-2">Preview Visual:</h4>
                <div 
                  className="border border-gray-300 rounded-lg p-4 bg-white overflow-auto max-h-[600px]"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
